import { Record } from "immutable";
import { ITaskData } from "common/records/Task";
import { fileFromJSON as fileFromJSON, FileRecord, IFileData, IFileRecord } from "common/records/File";
import { DateStringNumber, Nullable } from "common/records";
import { ImageableType } from "common/api/image.service";
import { checkDate } from "common/helpers/Date";

/**
 * A staging payload for a line-item image: the raw JSON shape plus the client-only `source_image_id`
 * (see {@link IImageRecord}). Used by the line-item image staging action; the field flows onto the
 * record via {@link imageFromJSON} and is stripped before any server write.
 */
export type StagedImagePayload = Partial<IImageData> & { source_image_id?: Nullable<number> };

export const imageFromJSON = (json: Partial<IImageData>): ImageRecord => {
  const record: IImageRecord = { ...(json as any) };

  if (json.file) {
    record.file = fileFromJSON(json.file);
  }

  if (json.created_at) {
    record.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    record.updated_at = checkDate(json.updated_at);
  }

  record.displayInProposal = record.display === "both" || record.display === "estimate";
  record.displayInAgreement = record.display === "both" || record.display === "contract";

  return new ImageRecord(record);
};

export const toJSON = (image: ImageRecord): Partial<IImageData> => {
  const img = image.toJSON() as Partial<IImageData>;

  if (img?.id !== undefined && img.id <= 0) {
    delete img.id;
  }

  return img;
};

export const toFormData = (record: ImageRecord): FormData => {
  const data = new FormData();
  if (record.id && record.id > 0) {
    data.set("image[id]", record.id.toString());
  }
  data.set("image[name]", record.name || "");
  if (record.file_queue && record.file_queue instanceof File) {
    data.set("image[file]", record.file_queue);
  }
  data.set("image[display]", record.display || "");
  data.set("image[imageable_id]", record.imageable_id?.toString() || "");
  data.set("image[imageable_type]", record.imageable_type || "");
  if (typeof record.sort_order === "number") data.set("image[sort_order]", record.sort_order.toString());
  if (record.folder_id && record.folder_id > 0) {
    data.set("image[folder_id]", record.folder_id.toString());
  }
  return data;
};

// Tied to the file record: the resizable derivatives are its keys other than the original (`url`,
// exposed here as "original") and the non-URL `filename`.
export type ImageSize = Exclude<keyof IFileRecord, "url" | "filename"> | "original";

export const getUrl = (image: ImageRecord, size: ImageSize): string => {
  if (!image || (!image.file && !image.file_queue)) {
    return "";
  }

  if (image.id <= 0 && image.file_queue) {
    // Staged files carry their object-URL under `file_queue.preview.url`; fall back to "" for a bare
    // File (no `.preview`) rather than throwing.
    return image.file_queue.preview?.url || "";
  }
  // "original" is the full-resolution upload, stored at the top-level `file.url`; the derivatives
  // are nested `{ url }` records.
  if (size === "original") {
    return (image.file?.url as string) || "";
  }
  return image.file?.getIn([size, "url"], "") as string;
};

export const setDisplay = (record: ImageRecord): ImageDisplayType => {
  let display: ImageDisplayType = "do_not_display";
  if (record.displayInProposal === true && record.displayInAgreement === true) {
    display = "both";
  } else if (record.displayInProposal === false && record.displayInAgreement === true) {
    display = "contract";
  } else if (record.displayInProposal === true && record.displayInAgreement === false) {
    display = "estimate";
  }
  return display;
};

export enum ImageDisplay {
  do_not_display = "do_not_display",
  contract = "contract",
  estimate = "estimate",
  both = "both",
  logo = "logo",
  inspection = "inspection",
}

export type ImageDisplayType = keyof typeof ImageDisplay;

export interface IImageData {
  id: number;
  uuid: string;
  folder_id: number;
  title: string;
  description: string;
  display: ImageDisplayType;
  data?: Nullable<File>;
  file: IFileData;
  file_size: number;
  imageable_id: number;
  imageable_type: string;
  md5_sum: string;
  name: string;
  sort_order: number;
  selected: boolean;
  loading: boolean;
  file_queue?: Nullable<File>;
  task?: ITaskData;

  _destroy: boolean;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
}

export interface IImageRecord {
  id: number;
  uuid: Nullable<string>;
  folder_id: Nullable<number>;
  title: Nullable<string>;
  name: Nullable<string>;
  description: Nullable<string>;
  display: Nullable<ImageDisplayType>;
  data?: Nullable<File>;
  file: Nullable<FileRecord>;
  file_queue: Nullable<any>;
  file_size: Nullable<any>;
  imageable_id: Nullable<number>;
  imageable_type: Nullable<ImageableType>;
  md5_sum: Nullable<string>;
  sort_order: Nullable<number>;
  /**
   * The job-photo id a staged copy was cloned from, stamped by the inspection photo picker at Add
   * time to drive within-session dedup. Client-only: it is never sent to the server (stripped in
   * `image.service.ts` `cleanProps`) and never comes back on reload, so it is on the record + defaults
   * only — deliberately NOT on {@link IImageData}.
   */
  source_image_id: Nullable<number>;
  _destroy: Nullable<boolean>;
  loading: boolean;
  selected: boolean;
  displayInProposal: Nullable<boolean>;
  displayInAgreement: Nullable<boolean>;
  created_at: Nullable<Date>;
  updated_at: Nullable<Date>;
}

export const defaultImageProps = {
  id: 0,
  uuid: null,
  folder_id: null,
  title: null,
  name: null,
  description: null,
  display: null,
  data: null,
  file: null,
  file_queue: null,
  file_size: null,
  imageable_id: null,
  imageable_type: null,
  md5_sum: null,
  sort_order: null,
  source_image_id: null,
  _destroy: null,
  loading: false,
  selected: false,
  displayInProposal: null,
  displayInAgreement: null,
  created_at: null,
  updated_at: null,
};

export class ImageRecord extends Record<IImageRecord>(defaultImageProps) implements IImageRecord {}
